import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { discountConfigState } from '@atoms/priceCalc';
import './ProductListing.css';
import products from './products';
import { priceBundle } from '../../Pages/WallProduct/data';
import GenAiIcon from './GenAiIcon';

// Real product -> its /products page. ASCII-only query (the wall derives the Hebrew
// name from ?p=), mirroring ProductsCatalog goProduct().
const productHref = (p) => {
  const q = new URLSearchParams();
  q.set('p', p.id);
  if (p.scene) q.set('scene', p.scene);
  if (p.cols) q.set('cols', p.cols);
  if (p.rows) q.set('rows', p.rows);
  if (p.bw) q.set('bw', '1');
  return `/products/${p.count}-frames?${q.toString()}`;
};

const ProductListing = () => {
  const navigate = useNavigate();
  const discountConfig = useRecoilValue(discountConfigState);
  const [currentProduct, setCurrentProduct] = useState(products[0]); // "modern living" by default
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageStyle, setImageStyle] = useState({});
  const [lastProduct, setLastProduct] = useState(null);
  const sectionRef = useRef(null);

  const preloadImages = () => {
    products.forEach((p) => { const img = new Image(); img.src = p.image; });
  };

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { preloadImages(); return undefined; }
    // Defer preloading the room renders until the section is near view (below-the-fold),
    // so they stay off the initial mobile load; the swap still feels instant.
    const io = new IntersectionObserver((entries) => {
      if (entries[0] && entries[0].isIntersecting) { preloadImages(); io.disconnect(); }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const getNextProductIndex = () => {
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * products.length);
    } while (products[nextIndex].id === currentProduct.id || products[nextIndex].id === lastProduct?.id);
    return nextIndex;
  };

  const handleChangeProduct = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); } // swap room, don't navigate to the product
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);

    const nextIndex = getNextProductIndex();
    setImageStyle({ opacity: 0.8 });
    setTimeout(() => setImageStyle({ opacity: 1 }), 400);

    setLastProduct(currentProduct);
    setCurrentProduct(products[nextIndex]);
  };

  const price = priceBundle(currentProduct.count);

  return (
    <>
      <style>{`
        @keyframes scaleShapeButton {
          0%, 100% { transform: translate(-50%, 50%) scale(1); }
          50% { transform: translate(-50%, 50%) scale(0.99); }
        }
      `}</style>
      <div className="product-listing" ref={sectionRef}>
        <div className="image-container" onClick={() => navigate(productHref(currentProduct))} style={{ cursor: 'pointer' }}>
          <img
            src={currentProduct.image}
            alt={`${currentProduct.name} - מסגרות תמונות לקיר`}
            style={{ ...imageStyle, objectPosition: currentProduct.pos || 'center' }}
            loading='lazy'
          />
          <button
            className={`change-shape-btn ${isAnimating ? 'scale-animation' : ''}`}
            onClick={handleChangeProduct}
            style={isAnimating ? { animation: 'scaleShapeButton 0.3s ease' } : {}}
          >
            <GenAiIcon isSpinning={isAnimating} />
            שינוי חדר
          </button>
        </div>
        <div className="product-info">
          {/* prices on the LEFT (discounted on top, struck-through regular below); Hebrew name on the RIGHT */}
          <div className="price-block">
            <div className="discount-and-tag">
              <p className="discounted-price">{price.final}₪</p>
              <p className="discount-tag">{discountConfig.emoji} מבצעי {discountConfig.promotionName}</p>
            </div>
            <p className="product-price">{price.regular}₪</p>
          </div>
          <p className="product-name">{currentProduct.name}</p>
        </div>
        <div className="upload-link">
          <a href="/products">קטלוג הקירות</a>
        </div>
      </div>
    </>
  );
};

export default ProductListing;
